import { Drawer, Modal } from '@mui/material';
import { styled, Theme } from '@mui/material/styles';

import FilterIcon from '~/assets/icons/svgs/filter.svg?react';
import { Button } from '~/components/ui/button';
import { DESKTOP_MIN_WIDTH, TABLET_MIN_WIDTH } from '~/constants/common';
import { COLORS } from '~/styles/colors';

type QRTypeProps = {
  $isMore?: boolean;
};

type QRTypeButtonProps = {
  $isSelected?: boolean;
  $isMore?: boolean;
};

export const StyledContainer = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  paddingTop: '40px',
  paddingBottom: '73px',
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    paddingTop: 32,
    paddingBottom: 60,
  },
  [theme.breakpoints.down(TABLET_MIN_WIDTH)]: {
    paddingTop: 10,
    paddingBottom: 50,
  },
}));

export const StyledTitleWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: 8,
  paddingBottom: 70,
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    paddingBottom: 24,
  },
}));

export const StyledMainContentWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '20px',
  width: '100%',
  backgroundColor: COLORS.grey500,
  borderRadius: '20px',
  padding: '16px',
  boxShadow: '0 0 20px 0 rgba(0, 0, 0, 0.05)',
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    padding: '8px',
    flexDirection: 'column',
    gap: '10px',
  },
}));

export const StyledLeftContainer = styled('div')(() => ({
  flex: 1,
  display: 'flex',
  gap: '20px',
  flexDirection: 'column',
  maxWidth: 'calc(100% - 400px)',
}));

export const StyledBlock = styled('div')(() => ({
  backgroundColor: COLORS.lightGray,
  borderRadius: '20px',
  padding: '26px 30px',
}));

export const StyledIphoneContainer = styled(StyledBlock)(() => ({
  width: 'fit-content',
  minWidth: '380px',
}));

export const StyledDesktopImageContainer = styled('div')(() => ({
  maxWidth: 320,
  marginTop: '24px',
  position: 'relative',
}));

export const StyledImageButton = styled(Button)(() => ({
  position: 'absolute',
  bottom: '20%',
  left: '50%',
  transform: 'translateX(-50%)',
  whiteSpace: 'nowrap',
}));

export const StyledQRTypesContainer = styled('div')(() => ({
  display: 'flex',
  gap: '19px',
  marginTop: '24px',
  flexWrap: 'wrap',
}));

export const StyledQRType = styled('div')<QRTypeProps>(({ $isMore }) => ({
  maxWidth: $isMore ? '83px' : '70px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

export const StyledQRTypeButton = styled('button')<QRTypeButtonProps>(({ $isSelected, $isMore }) => {
  let backgroundColor: string = COLORS.white;

  if ($isSelected) {
    backgroundColor = COLORS.blue50;
  } else if ($isMore) {
    backgroundColor = COLORS.primary;
  }

  return {
    backgroundColor,
    width: '70px',
    height: '70px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${$isSelected ? COLORS.blue100 : COLORS.grey250}`,
    borderRadius: '16px',
    boxSizing: 'border-box',
    padding: '10px',
    marginBottom: '6px',
    cursor: 'pointer',
  };
});

export const StyledLeftBottomBlock = styled(StyledBlock)(() => ({
  borderRadius: '20px',
  padding: '26px 30px',
  flex: 1,
}));

export const StyledLoginToCreateContainer = styled('div')(() => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
}));

export const StyledModal = styled(Modal)(() => ({
  '& .MuiModal-backdrop': {
    backgroundColor: 'rgba(217, 217, 217, 0.2)',
  },
}));

export const StyledLastStepModalContent = styled('div')(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  boxShadow: '0 0 20px 0 rgba(0, 0, 0, 0.05)',
  width: '100%',
  maxWidth: '510px',
  borderRadius: '20px',
  padding: '30px',
  backgroundColor: COLORS.white,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

export const StyledModalBlurred = styled('div')(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backdropFilter: 'blur(3px)',
}));

export const StyledDrawer = styled(Drawer)(() => ({
  '& .MuiDrawer-paper': {
    padding: '20px 10px',
    borderRadius: '20px 20px 0 0',
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(3px)',
    backgroundColor: 'rgba(217, 217, 217, 0.2)',
  },
}));

export const StyledQRImage = styled('img')(() => ({
  width: '200px',
  aspectRatio: '1',
  position: 'absolute',
  top: 'calc(50% - 10px)',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  objectFit: 'contain',
}));

export const StyledWebsiteURL = styled('div')(() => ({
  position: 'absolute',
  width: 'calc(100% - 72px)',
  borderRadius: '100px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '0 16px',
  height: '40px',
  border: `1px solid ${COLORS.textMain}`,
  left: '50%',
  transform: 'translateX(-50%)',
  top: '62px',
}));

export const StyledFilterIcon = styled(FilterIcon)(() => ({
  flexShrink: 0,
}));

export const StyledLoaderQr = styled('img')(() => ({
  position: 'absolute',
  width: 'calc(100% - 32px) !important',
  left: '50%',
  transform: 'translateX(-50%)',
  top: '171px',
}));
